/**
 * callableErrors — RW-1556 §9: ONE place that turns a callable failure into
 * user-facing copy.
 *
 * ~15 Forms/Sign surfaces rendered raw `e.message` into their error banners —
 * fine when the server wrote intentional copy (our callables craft
 * failed-precondition / resource-exhausted messages for humans), ugly when a
 * gRPC code name or stack head leaked through. Policy here:
 *   - codes our backend writes INTENTIONAL copy for keep the server message
 *     as the headline when it reads like a sentence;
 *   - everything else gets a plain-English headline for the code, with the
 *     raw message preserved as a DISCLOSABLE detail (never the headline).
 *
 * reportDeadEnd() additionally files a structured errorLogs event for
 * dead-end failures (Rule 26 §4 — the reportDeadLink pattern: auto-tickets
 * to Linear), deduped per session per surface/action.
 */
import { logErrorToFirestore } from '../components/ErrorBoundary';

const CODE_COPY = {
  'functions/unauthenticated': 'Your session expired — sign in again to continue.',
  'functions/permission-denied': "You don't have permission to do this.",
  'functions/failed-precondition': "This isn't available yet — a required setup step is missing.",
  'functions/resource-exhausted': 'Too many requests right now — wait a moment and try again.',
  'functions/not-found': "That record couldn't be found — it may have been removed.",
  'functions/invalid-argument': "Something about this request wasn't valid.",
  'functions/already-exists': 'That already exists.',
  'functions/deadline-exceeded': 'The request timed out — try again.',
  'functions/unavailable': 'The service is unreachable right now — try again in a minute.',
  'functions/cancelled': 'The request was cancelled.',
  'functions/internal': 'Something went wrong on our side — try again, and report it if it keeps happening.',
};

/* Codes whose server messages are deliberately user-facing in this codebase
   (send guards, quota copy, validation) — keep them as the headline. */
const INTENTIONAL_CODES = new Set([
  'functions/failed-precondition',
  'functions/resource-exhausted',
  'functions/invalid-argument',
  'functions/not-found',
  'functions/permission-denied',
  'functions/already-exists',
]);

/* The canonical gRPC/Firebase status names. The client SDK falls back to the
   bare code name as the message whenever the response body isn't JSON — a dead
   container, a Cloud Run 504, a network failure — and it emits the lowercase
   kebab form there, not the SCREAMING_SNAKE one. Matched as an explicit list
   rather than a shape so a deliberate one-word server message ("Expired.",
   "Unauthorized.") still reads as human copy. */
const CODE_NAMES = new Set([
  'ok', 'cancelled', 'unknown', 'invalid-argument', 'deadline-exceeded',
  'not-found', 'already-exists', 'permission-denied', 'resource-exhausted',
  'failed-precondition', 'aborted', 'out-of-range', 'unimplemented',
  'internal', 'unavailable', 'data-loss', 'unauthenticated',
]);

/* A bare status name is never user-facing text (RW-1556 §9), in any casing. */
function isBareCodeName(msg) {
  return CODE_NAMES.has(String(msg).trim().toLowerCase().replace(/_/g, '-'));
}

/* "Reads like a sentence" — no stack frames, not a bare code name. Length is
   checked separately (looksHuman) so a validation verdict can opt out of it. */
function readsHuman(msg) {
  if (!msg) return false;
  if (/^[A-Z_]+$/.test(msg)) return false; // "INTERNAL", "UNAVAILABLE"
  if (isBareCodeName(msg)) return false; // "internal", "deadline-exceeded"
  if (/\bat .+ \(.+\)/.test(msg)) return false; // stack head
  return true;
}

/* "Reads like a sentence" — short, no stack frames, not a bare code name. */
function looksHuman(msg) {
  if (!msg || msg.length > 260) return false;
  return readsHuman(msg);
}

/* RW-2166 — a validation verdict is the ONE intentional message that is
   routinely long: the Forms save callable lists EVERY problem in a single
   invalid-argument string ("Form spec is invalid — 6 problem(s): …"), which
   blows past the 260-char sentence cap on any real template. Dropping it left
   the admin with "Something about this request wasn't valid." and no clue
   which field. Keep the first 400 chars as disclosable detail instead. */
const VALIDATION_DETAIL_CODE = 'functions/invalid-argument';
const VALIDATION_DETAIL_MAX = 400;

/**
 * @param {*} e         the caught callable error
 * @param {string} action  imperative fragment for the fallback headline,
 *                         e.g. 'send this form' → "Couldn't send this form."
 * @returns {{ headline: string, detail: string|null, code: string }}
 */
export function friendlyCallableError(e, action = 'complete this action') {
  const code = String((e && e.code) || '');
  const raw = String((e && e.message) || '').trim();
  if (INTENTIONAL_CODES.has(code) && looksHuman(raw)) {
    return { headline: raw, detail: null, code };
  }
  const headline = CODE_COPY[code] || `Couldn't ${action}.`;
  // The raw message survives as disclosable detail when it reads like a sentence
  // — or, for a code we have no copy for, even when it doesn't, since it is then
  // the only diagnostic the user gets. Never when it is only the code name.
  let detail = raw && raw !== headline && looksHuman(raw)
    ? raw
    : (raw && !CODE_COPY[code] && !isBareCodeName(raw) ? raw.slice(0, 300) : null);
  // An over-long validation verdict survives (truncated) — it is the only
  // thing that says WHICH field is wrong (RW-2166).
  if (detail === null && code === VALIDATION_DETAIL_CODE && readsHuman(raw)) {
    detail = raw.slice(0, VALIDATION_DETAIL_MAX);
  }
  return { headline, detail, code };
}

/**
 * friendlyCallableError flattened to one line for a toast: the headline, then
 * the detail when there is one. For surfaces where the reason matters to the
 * reader (admin tools), so a headline-only toast would drop the diagnostic.
 */
export function friendlyCallableText(e, action) {
  const { headline, detail } = friendlyCallableError(e, action);
  return detail ? `${headline} ${detail}` : headline;
}

const reportedDeadEnds = new Set();

/**
 * Rule 26 §4 — a user action that dead-ends (send failed, load failed with no
 * retry succeeding) self-reports to errorLogs so it auto-tickets to Linear.
 * Record IDs only, never record contents (Rule 22).
 */
export function reportDeadEnd(surface, action, e, recordId = '') {
  const key = `${surface}/${action}`;
  if (reportedDeadEnds.has(key)) return;
  reportedDeadEnds.add(key);
  const code = String((e && e.code) || 'unknown');
  logErrorToFirestore({
    type: 'callable-dead-end',
    message: `[dead-end] ${key} failed (${code})`,
    module: String(surface),
    action: String(action),
    code,
    recordId: String(recordId || ''),
    detail: String((e && e.message) || '').slice(0, 300),
  }).catch(() => {});
}
